import { ANPREvent } from 'domain/anpr-event'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Tag } from 'primereact/tag'
import MercosulPlate from 'shared/mercosul-plate/mercosul-license-plate'
import { getColorInfo } from './anpr-event-formatters'
import { getVehicleType } from '../util/getVehicleTypeTags'
import { isSpeedViolation } from '../util/speed-violation-filter'
import { isLowConfidence } from '../utils/confidence-level'

interface ANPREventGridItemProps {
  event: ANPREvent
  onViewDetails: (event: ANPREvent) => void
  onImageClick: (url: string) => void
  onExport: () => void
}

const ANPREventGridItem = ({ event, onViewDetails, onImageClick, onExport }: ANPREventGridItemProps) => {
  const date = new Date(event.createdDate)
  const colorInfo = getColorInfo(event.vehicleColor)
  const violation = isSpeedViolation(event)

  return (
    <div className='col-12 sm:col-6 lg:col-4 xl:col-3 flex'>
      <Card className='event-card'>
        <div className='event-card-header'>
          <div className='flex align-items-center gap-2'>
            {getVehicleType(event.vehicleType)}
            <span className='event-camera-badge'>
              <i className='pi pi-video text-xs' />
              {event.cameraName}
            </span>
          </div>
          <Tag
            value={date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            icon='pi pi-clock'
            className='event-time-tag'
          />
        </div>

        {event.illegalName && (
          <Tag
            severity='danger'
            icon='pi pi-exclamation-triangle'
            value={event.illegalName}
            className='event-illegal-tag'
          />
        )}

        <div
          className='event-card-image'
          onClick={() => event.detectionImagePath && onImageClick(event.detectionImagePath)}
        >
          {event.detectionImagePath ? (
            <div className='event-image-wrapper'>
              <img src={event.detectionImagePath} alt='Detecção' className='event-main-image' />
              <div className='event-image-overlay'>
                <i className='pi pi-search-plus' />
              </div>
            </div>
          ) : (
            <div className='event-no-image'>
              <i className='pi pi-image' />
              <span>Sem imagem disponível</span>
            </div>
          )}
        </div>

        <div className='event-card-plate'>
          <MercosulPlate licensePlate={event.licensePlate} size='M' />
          {event.confidenceLevel !== null && isLowConfidence(event.confidenceLevel) && (
            <Tag
              severity='warning'
              icon='pi pi-question-circle'
              value={`Confiança baixa: ${event.confidenceLevel}%`}
              className='event-low-confidence-tag'
            />
          )}
        </div>

        <div className='event-card-info'>
          <div className='event-info-row'>
            <span className='event-info-label'>
              <i className='pi pi-calendar' />
              Data
            </span>
            <span className='event-info-value'>{date.toLocaleDateString('pt-BR')}</span>
          </div>

          <div className='event-info-row'>
            <span className='event-info-label'>
              <i className='pi pi-palette' />
              Cor
            </span>
            <span className='event-info-value'>
              <span className='event-color-indicator' style={{ backgroundColor: colorInfo.color }} />
              {colorInfo.label}
            </span>
          </div>

          {event.speed > 0 && (
            <div className='event-info-row'>
              <span className='event-info-label' style={violation ? { color: 'var(--red-500)' } : undefined}>
                <i className='pi pi-gauge' />
                Velocidade
              </span>
              <span className='event-info-value' style={violation ? { color: 'var(--red-500)', fontWeight: '600' } : undefined}>
                {event.speed} km/h
              </span>
            </div>
          )}

          {event.direction && !(event.speed > 0) && (
            <div className='event-info-row'>
              <span className='event-info-label'>
                <i className='pi pi-arrow-right' />
                Direção
              </span>
              <span className='event-info-value'>{event.direction}</span>
            </div>
          )}

          {event.eventName && (
            <div className='event-info-row'>
              <span className='event-info-label'>
                <i className='pi pi-tag' />
                Evento
              </span>
              <span className='event-info-value text-sm'>{event.eventName}</span>
            </div>
          )}
        </div>

        <div className='event-card-actions'>
          <Button
            icon='pi pi-eye'
            label='Detalhes'
            className='p-button-sm w-full'
            onClick={() => onViewDetails(event)}
          />
          <Button
            icon='pi pi-download'
            className='p-button-sm p-button-outlined'
            onClick={onExport}
            tooltip='Baixar Imagens'
            tooltipOptions={{ position: 'top' }}
          />
        </div>
      </Card>
    </div>
  )
}

export default ANPREventGridItem